import { showAlert } from 'utils/ErrorMessage';
import { showSuccessAlert } from 'utils/SuccessMessage';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from "yup";
import { uploadGoodsReturnTransferDetails } from '../../Service/ApiService';

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
];


export const goodAndReturnsSchema = Yup.object().shape({
  // party: Yup.object()
  //   .nullable()
  //   .required("Party is required"),

  LRNumber: Yup.string()
    .trim()
    .required("LR Number is required"),

  LRDate: Yup.string()
    .required("LR Date is required"),

  Transporter: Yup.string()
    .trim()
    .required("Transporter is required"),

  eWayBill: Yup.string()
    .trim()
    .required("E-Way Bill is required"),

  discountPercentage: Yup.number()
    .typeError("Discount Percentage must be a number")
    .min(0, "Discount Percentage cannot be negative")
    .max(100, "Discount Percentage cannot exceed 100"),

  lrCopyFile: Yup.mixed()
    .required("LR Copy is required")
    .test(
      "fileType",
      "Only PDF files are allowed",
      (value) => value && ALLOWED_FILE_TYPES.includes(value.type)
    ),

  debitNoteFile: Yup.mixed()
    .required("Debit Note is required")
    .test(
      "fileType",
      "Only PDF files are allowed",
      (value) => value && ALLOWED_FILE_TYPES.includes(value.type)
    ),
});

const GoodAndRetunsTransferController = () => {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user'));
  const location = useLocation();
  const [loader, setLoader] = useState(false);

  const [LRNumber, setLRNumber] = useState('');
  const [LRDate, setLRDate] = useState('');
  const [Transporter, setTransporter] = useState('');
  const [eWayBill, setEWayBill] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [lrCopyFile, setLRCopyFile] = useState(null);
  const [debitNoteFile, setDebitNoteFile] = useState(null);
  const [errors, setErrors] = useState({});
  const { partyCode, fromPartyCode, goodsReturnInvoiceDtlsId, deliveryNumber, invoiceNumber } = location.state || {};

  const handleClearButtonClick = () => {
    setLRNumber('');
    setLRDate('');
    setTransporter('');
    setEWayBill('');
    setDiscountPercentage('');
    setLRCopyFile(null);
    setDebitNoteFile(null);
  };

  const handleSubmit = async () => {
    try {
      setErrors({}); // clear previous errors
      setLoader(true);
      // --------------------
      // 1. Yup Validation
      // --------------------
      await goodAndReturnsSchema.validate(
        {
          partyCode,
          LRNumber,
          LRDate,
          Transporter,
          eWayBill,
          discountPercentage,
          lrCopyFile,
          debitNoteFile,
        },
        { abortEarly: false }
      );

      // --------------------
      // 2. Prepare API Payload
      // --------------------
      const payload = {
        deliveryNumber: deliveryNumber || '',
        // return_option: return_option || '',
        user_code: user.userCode,
        // order_number: order_number || '',
        party_code: partyCode || '',
        from_party_code: fromPartyCode || '',
        goodsReturnInvoiceDtlsId: goodsReturnInvoiceDtlsId || '',
        invoice_number: invoiceNumber || '',
        // invoice_date: goodsReturnsData?.invoice_date || '',
        // barcode: goodsReturnsData?.barcode || '',
        // quantity: goodsReturnsData?.quantity || 0,
        // material_code: goodsReturnsData?.material_code || '',
        // material_description: goodsReturnsData?.material_desc || '',
        // batch: goodsReturnsData?.batch || '',

        lr_number: LRNumber,
        lr_date: LRDate,
        transporter: Transporter,
        e_way_bill: eWayBill,
        discount_percentage: discountPercentage || 0,

        lr_copy: lrCopyFile,                   // File object
        debit_note_copy: debitNoteFile,        // File object
      };

      // --------------------
      // 3. API Call
      // --------------------
      const res = await uploadGoodsReturnTransferDetails(payload);

      if (res?.success) {
        setLoader(false);
        showSuccessAlert(res.message, 'Message');
        navigate('/good-returns-list');

        // Optional: clear form after success
        handleClearButtonClick();
      } else {
        setLoader(false);
        showAlert(res?.message || "Failed to submit goods return transfer");
        setErrors({ form: res?.message || "Something went wrong" });
      }

    } catch (err) {
      setLoader(false);
      // --------------------
      // Yup validation errors
      // --------------------
      if (err?.inner) {
        const fieldErrors = {};
        err.inner.forEach((e) => {
          fieldErrors[e.path] = e.message;
        });
        setErrors(fieldErrors);
      } else {
        // --------------------
        // API / Network errors
        // --------------------
        setErrors({
          form: err?.message || "Failed to submit goods return transfer",
        });
      }
    }
  };


  return {
    loader,
    partyCode,
    LRNumber,
    setLRNumber,
    LRDate,
    setLRDate,
    Transporter,
    setTransporter,
    eWayBill,
    setEWayBill,
    discountPercentage,
    setDiscountPercentage,

    lrCopyFile,
    setLRCopyFile,
    debitNoteFile,
    setDebitNoteFile,

    handleClearButtonClick,
    handleSubmit,
    errors,
  };
};

export default GoodAndRetunsTransferController;
